import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, AreaChart, Area, CartesianGrid,
} from 'recharts';
import { ArrowUpRight, DollarSign, TrendingUp, Users, FileText, Coins, Loader2, Building2, AlertTriangle, HardDrive, FileScan, Workflow, Zap, CheckCircle2 } from 'lucide-react';
import { brandColors } from '../components/Brand';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const COLORS = ['#1e3a5f', '#2563eb', '#0891b2', '#7c3aed', '#059669'];

const Kpi = ({ icon: Icon, label, value, color, testid }) => (
  <Card data-testid={testid}><CardContent className="pt-6">
    <div className="flex items-center justify-between">
      <div><p className="text-sm text-slate-500">{label}</p><p className="text-2xl font-bold text-slate-900">{value}</p></div>
      <div className={`p-3 rounded-xl ${color}`}><Icon className="w-6 h-6 text-white" /></div>
    </div></CardContent></Card>
);

const QUOTA_RESOURCE_META = {
  storage: { label: 'Storage', Icon: HardDrive, cls: 'bg-teal-100 text-teal-700' },
  ai_credits: { label: 'AI Credits', Icon: Coins, cls: 'bg-amber-100 text-amber-700' },
  ocr: { label: 'OCR', Icon: FileScan, cls: 'bg-indigo-100 text-indigo-700' },
  workflows: { label: 'Workflows', Icon: Workflow, cls: 'bg-purple-100 text-purple-700' },
  api_calls: { label: 'API Calls', Icon: Zap, cls: 'bg-rose-100 text-rose-700' },
};

const RevenueDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [quota, setQuota] = useState(null);

  const load = useCallback(async () => {
    try { const res = await api.getRevenueSummary(getAuthHeaders()); setS(res.data); }
    catch (e) { toast.error('Failed to load revenue summary'); }
    try { const q = await api.revQuotaHits(30, getAuthHeaders()); setQuota(q.data); }
    catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  const consumption = (s.monthly_trend || []).map((m) => ({ month: m.month, revenue: m.revenue }));

  return (
    <div data-testid="revenue-dashboard" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>Revenue Dashboard</h1>
        <p className="text-slate-600 mt-1">Monetization analytics across all tenants.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="MRR" value={fmtINR(s.mrr)} color="bg-blue-600" testid="kpi-mrr" />
        <Kpi icon={TrendingUp} label="ARR" value={fmtINR(s.arr)} color="bg-indigo-600" testid="kpi-arr" />
        <Kpi icon={Building2} label="Active Tenants" value={s.total_tenants} color="bg-cyan-600" testid="kpi-tenants" />
        <Kpi icon={Users} label="Active Subscriptions" value={s.active_subscriptions} color="bg-emerald-600" testid="kpi-subs" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-by-type"><CardHeader><CardTitle>Revenue by Type (MRR)</CardTitle></CardHeader>
          <CardContent>
            {(s.revenue_by_type || []).some((t) => t.value > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={s.revenue_by_type.filter((t) => t.value > 0)} dataKey="value" nameKey="type" cx="50%" cy="50%" outerRadius={95} label>
                    {s.revenue_by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} /><Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-500 text-sm py-16 text-center">No revenue recorded yet.</p>}
          </CardContent></Card>
        <Card data-testid="monthly-trend"><CardHeader><CardTitle>Monthly Revenue Trend</CardTitle></CardHeader>
          <CardContent>
            {consumption.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={consumption}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill={brandColors.primary} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-500 text-sm py-16 text-center">No invoices yet.</p>}
          </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle>Top Customers</CardTitle></CardHeader>
          <CardContent>
            {(s.top_customers || []).length ? (
              <Table data-testid="top-customers-table"><TableHeader><TableRow>
                <TableHead>Tenant</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">MRR</TableHead>
                <TableHead className="text-right">Credits Used</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
                <TableBody>{s.top_customers.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{c.tenant_name}</TableCell>
                    <TableCell><Badge variant="outline">{c.plan}</Badge></TableCell>
                    <TableCell className="text-right">{fmtINR(c.mrr)}</TableCell>
                    <TableCell className="text-right">{Number(c.ai_credits_used).toLocaleString()}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700">{c.status}</Badge></TableCell>
                  </TableRow>
                ))}</TableBody></Table>
            ) : <p className="text-slate-500 text-sm py-10 text-center">No active subscriptions.</p>}
          </CardContent></Card>
        <Card><CardHeader><CardTitle>Consumption Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                <div className="flex items-center gap-2 text-amber-700"><Coins className="w-4 h-4" /> AI Credits Consumed</div>
                <p className="text-2xl font-bold text-amber-800 mt-1">{Number(s.ai_credits_consumed).toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-4">
                <div className="flex items-center gap-2 text-indigo-700"><FileText className="w-4 h-4" /> OCR Pages Consumed</div>
                <p className="text-2xl font-bold text-indigo-800 mt-1">{Number(s.ocr_pages_consumed || 0).toLocaleString()}</p>
              </div>
            </div>
            {consumption.length ? (
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={consumption}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={brandColors.primary} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={brandColors.primary} stopOpacity={0} />
                  </linearGradient></defs>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Area type="monotone" dataKey="revenue" stroke={brandColors.primary} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : null}
          </CardContent></Card>
      </div>

      {/* Quota Limit Hits — proactive upsell signal */}
      <Card data-testid="quota-hits-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Quota Limit Hits
            <span className="text-sm font-normal text-slate-400">· last {quota?.window_days || 30} days</span>
            {quota && <Badge className="ml-auto bg-amber-100 text-amber-700" data-testid="quota-hits-total">{quota.total_hits} total</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!quota || quota.total_hits === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center">No tenants have hit their plan limits recently. 🎉</p>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2" data-testid="quota-by-resource">
                {quota.by_resource.map((r) => {
                  const meta = QUOTA_RESOURCE_META[r.resource] || { label: r.resource, Icon: AlertTriangle, cls: 'bg-slate-100 text-slate-700' };
                  const Icon = meta.Icon;
                  return (
                    <span key={r.resource} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${meta.cls}`}>
                      <Icon className="w-4 h-4" /> {meta.label}: {r.count}
                    </span>
                  );
                })}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-2">Tenants hitting limits (upsell targets)</p>
                <Table data-testid="quota-top-tenants-table"><TableHeader><TableRow>
                  <TableHead>Tenant</TableHead><TableHead>Plan</TableHead>
                  <TableHead>Resources</TableHead><TableHead className="text-right">Hits</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow></TableHeader>
                  <TableBody>{quota.top_tenants.map((t) => (
                    <TableRow key={t.tenant_id} data-testid={`quota-tenant-${t.tenant_id}`}>
                      <TableCell className="font-medium">{t.tenant_name}</TableCell>
                      <TableCell><Badge variant="outline">{t.plan || '—'}</Badge></TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {Object.entries(t.resources).map(([k, v]) => `${(QUOTA_RESOURCE_META[k] || {}).label || k} (${v})`).join(', ')}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-amber-700">{t.count}</TableCell>
                      <TableCell>
                        {t.resolved ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium" data-testid={`quota-resolved-${t.tenant_id}`}>
                            <CheckCircle2 className="w-4 h-4" /> Resolved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-sm font-medium" data-testid={`quota-pending-${t.tenant_id}`}>
                            <AlertTriangle className="w-4 h-4" /> {t.unresolved_count ?? t.count} pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/revenue-admin?tab=subs&limitTenant=${t.tenant_id}`)}
                          data-testid={`increase-limit-${t.tenant_id}`}>
                          <ArrowUpRight className="w-4 h-4 mr-1" /> Increase limit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody></Table>
              </div>
            </div>
          )}
        </CardContent></Card>
    </div>
  );
};

export default RevenueDashboard;
